import React, { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useSelector } from "react-redux";
import Switch from "react-switch";
import Swal from "sweetalert2";

import PageTitle from "../../layouts/PageTitle";
import { selectUserRole } from "../../../store/authSlice";
import {
  cwListProjects,
  cwGetRoster,
  cwSetWorkerStatus,
  cwSwitchWorkerProject,
} from "../../../services/contractWorkers";

// ─────────────────────────────────────────────────────────────────────────────
// Contract Workers → Worker Roster
// One row per worker with the working-days count for the selected month.
// Super-admin can flip the status switch (enable/disable) and move a worker to
// another project inline. "View" opens that worker's month calendar.
// ─────────────────────────────────────────────────────────────────────────────

const MONTHS = [
  "January",
  "February",
  "March",
  "April",
  "May",
  "June",
  "July",
  "August",
  "September",
  "October",
  "November",
  "December",
];

const ContractWorkerRoster = () => {
  const navigate = useNavigate();
  const role = useSelector(selectUserRole);
  const isSuperAdmin = role === "super-admin";

  const now = new Date();
  const [projects, setProjects] = useState([]);
  const [filterProjectId, setFilterProjectId] = useState("");
  const [search, setSearch] = useState("");
  const [year, setYear] = useState(now.getFullYear());
  const [month, setMonth] = useState(now.getMonth() + 1);
  const [roster, setRoster] = useState([]);
  const [loading, setLoading] = useState(false);
  const [busyId, setBusyId] = useState(null);

  const monthStr = `${year}-${String(month).padStart(2, "0")}`;

  useEffect(() => {
    cwListProjects()
      .then((res) => setProjects(res.data?.projects || []))
      .catch(() => setProjects([]));
  }, []);

  const load = useCallback(() => {
    setLoading(true);
    cwGetRoster(
      {
        month: monthStr,
        includeDisabled: "true",
        projectId: filterProjectId || undefined,
      },
      filterProjectId || undefined,
    )
      .then((res) => setRoster(res.data?.roster || []))
      .catch(() => setRoster([]))
      .finally(() => setLoading(false));
  }, [monthStr, filterProjectId]);

  useEffect(() => {
    load();
  }, [load]);

  const toggleStatus = async (w) => {
    setBusyId(w.workerId);
    try {
      await cwSetWorkerStatus(w.workerId, !w.status);
      load();
    } catch (err) {
      Swal.fire({
        icon: "error",
        title: "Error",
        text: err?.response?.data?.message || "Failed to update status.",
      });
    } finally {
      setBusyId(null);
    }
  };

  const switchProject = async (w, projectId) => {
    if (!projectId || projectId === w.project?._id) return;
    const target = projects.find((p) => p._id === projectId);
    const confirm = await Swal.fire({
      icon: "warning",
      title: `Move ${w.name}?`,
      html: `This moves <b>${w.name}</b> from <b>${
        w.project?.projectName || "—"
      }</b> to <b>${target?.projectName || "the selected project"}</b>.`,
      showCancelButton: true,
      confirmButtonText: "Move worker",
    });
    if (!confirm.isConfirmed) {
      load(); // reset the <select> back to the current project
      return;
    }
    setBusyId(w.workerId);
    try {
      await cwSwitchWorkerProject(w.workerId, projectId);
      load();
    } catch (err) {
      Swal.fire({
        icon: "error",
        title: "Error",
        text: err?.response?.data?.message || "Failed to move worker.",
      });
    } finally {
      setBusyId(null);
    }
  };

  const visible = roster.filter((w) =>
    w.name?.toLowerCase().includes(search.toLowerCase()),
  );
  const years = Array.from({ length: 5 }, (_, i) => now.getFullYear() - i);

  return (
    <>
      <PageTitle activeMenu="Worker Roster" motherMenu="Contract Workers" />
      <div className="card">
        <div className="card-header d-flex flex-wrap gap-2 align-items-center">
          <h4 className="card-title mb-0 me-auto">Worker Roster</h4>
          <select
            className="form-select form-select-sm"
            style={{ width: 120 }}
            value={month}
            onChange={(e) => setMonth(Number(e.target.value))}
          >
            {MONTHS.map((m, i) => (
              <option key={m} value={i + 1}>
                {m}
              </option>
            ))}
          </select>
          <select
            className="form-select form-select-sm"
            style={{ width: 90 }}
            value={year}
            onChange={(e) => setYear(Number(e.target.value))}
          >
            {years.map((y) => (
              <option key={y} value={y}>
                {y}
              </option>
            ))}
          </select>
          <select
            className="form-select form-select-sm"
            style={{ width: 180 }}
            value={filterProjectId}
            onChange={(e) => setFilterProjectId(e.target.value)}
          >
            <option value="">All Projects</option>
            {projects.map((p) => (
              <option key={p._id} value={p._id}>
                {p.projectName}
              </option>
            ))}
          </select>
          <input
            className="form-control form-control-sm"
            style={{ width: 170 }}
            placeholder="Search worker…"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
          {isSuperAdmin && (
            <button
              className="btn btn-primary btn-sm"
              onClick={() => navigate("/contract-workers/add")}
            >
              + Create Worker
            </button>
          )}
        </div>
        <div className="card-body">
          {loading ? (
            <div className="text-center py-4">Loading…</div>
          ) : visible.length === 0 ? (
            <div className="text-center text-muted py-4">
              No workers found.
            </div>
          ) : (
            <div className="table-responsive">
              <table className="table table-hover align-middle">
                <thead>
                  <tr>
                    <th>Worker</th>
                    <th>Contract Company</th>
                    <th>Work Profile</th>
                    <th>Shift</th>
                    <th>Project</th>
                    <th className="text-center">
                      Working Days
                      <br />
                      <small className="text-muted">
                        {MONTHS[month - 1]} {year}
                      </small>
                    </th>
                    {isSuperAdmin && <th className="text-center">Status</th>}
                    <th aria-label="Actions" />
                  </tr>
                </thead>
                <tbody>
                  {visible.map((w) => (
                    <tr key={w.workerId}>
                      <td className="fw-semibold text-capitalize">{w.name}</td>
                      <td>{w.contractCompany}</td>
                      <td>{w.workProfile}</td>
                      <td>
                        <span className="badge bg-light text-dark">
                          {w.shift || "Morning"}
                        </span>
                      </td>
                      <td>
                        {isSuperAdmin ? (
                          <select
                            className="form-select form-select-sm"
                            style={{ minWidth: 150 }}
                            value={w.project?._id || ""}
                            disabled={busyId === w.workerId}
                            onChange={(e) => switchProject(w, e.target.value)}
                          >
                            {projects.map((p) => (
                              <option key={p._id} value={p._id}>
                                {p.projectName}
                              </option>
                            ))}
                          </select>
                        ) : (
                          w.project?.projectName || "—"
                        )}
                      </td>
                      <td className="text-center fw-bold">
                        {w.workingDays}
                        {(w.halfDays || w.leaveDays) ? (
                          <small className="d-block text-muted fw-normal">
                            {w.halfDays ? `${w.halfDays} half` : ""}
                            {w.halfDays && w.leaveDays ? " · " : ""}
                            {w.leaveDays ? `${w.leaveDays} leave` : ""}
                          </small>
                        ) : null}
                      </td>
                      {isSuperAdmin && (
                        <td className="text-center">
                          <Switch
                            onChange={() => toggleStatus(w)}
                            checked={!!w.status}
                            disabled={busyId === w.workerId}
                            onColor="#4d6542"
                            offColor="#ff5e5e"
                            uncheckedIcon={false}
                            checkedIcon={false}
                            height={20}
                            width={40}
                          />
                        </td>
                      )}
                      <td className="text-end" style={{ whiteSpace: "nowrap" }}>
                        {isSuperAdmin && (
                          <button
                            className="btn btn-xs btn-outline-secondary me-1"
                            onClick={() =>
                              navigate(
                                `/contract-workers/edit/${w.workerId}`,
                              )
                            }
                          >
                            Edit
                          </button>
                        )}
                        <button
                          className="btn btn-xs btn-outline-primary"
                          onClick={() =>
                            navigate(
                              `/contract-workers/worker/${w.workerId}`,
                            )
                          }
                        >
                          View
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>
    </>
  );
};

export default ContractWorkerRoster;
